import React, { useState } from 'react';
import { useAuth } from './context/AuthContext';

// Self-service profile page: lets the signed-in user change their password
// or permanently delete their account. Both actions go through dedicated
// endpoints under /api/auth that re-verify the current password.
const ProfilePage = () => {
  const { user, role, changePassword, deleteAccount, logout } = useAuth();

  // --- Change password state ----------------------------------------------
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  const onChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (!currentPassword || !newPassword) {
      setPwError('Please fill in both fields.');
      return;
    }
    if (newPassword.length < 8) {
      setPwError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('New password and confirmation do not match.');
      return;
    }
    setPwSubmitting(true);
    const res = await changePassword({ currentPassword, newPassword });
    setPwSubmitting(false);
    if (res.ok) {
      setPwSuccess('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPwError(res.error || 'Could not change password.');
    }
  };

  // --- Delete account state -----------------------------------------------
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const isSuperuser = role === 'superuser';

  const onDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm.');
      return;
    }
    if (!deletePassword) {
      setDeleteError('Please enter your password.');
      return;
    }
    setDeleteSubmitting(true);
    const res = await deleteAccount({ password: deletePassword });
    setDeleteSubmitting(false);
    if (res.ok) {
      // Auth state already cleared in context; the LoginGate will pick it up
      // automatically on the next render. A best-effort logout call ensures
      // any server-side hooks observe the sign-out as well.
      logout();
    } else {
      setDeleteError(res.error || 'Could not delete account.');
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-2 sm:px-4">
      <h1 className="text-2xl font-bold text-slate-800 mb-1">Profile</h1>
      <p className="text-sm text-slate-500 mb-6">Manage your account settings.</p>

      {/* Account info card */}
      <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 sm:p-6 mb-6">
        <h2 className="font-semibold text-slate-800 mb-3">Account</h2>
        <dl className="text-sm grid grid-cols-3 gap-y-2">
          <dt className="text-slate-500">Username</dt>
          <dd className="col-span-2 text-slate-800">{user?.username || '—'}</dd>
          <dt className="text-slate-500">Email</dt>
          <dd className="col-span-2 text-slate-800">{user?.email || '—'}</dd>
          <dt className="text-slate-500">Role</dt>
          <dd className="col-span-2">
            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">{role}</span>
          </dd>
        </dl>
      </section>

      {/* Change password card */}
      <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 sm:p-6 mb-6">
        <h2 className="font-semibold text-slate-800 mb-3">Change password</h2>
        <form onSubmit={onChangePassword} className="space-y-3">
          <div>
            <label className="block text-sm text-slate-600 mb-1">Current password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-400 mt-1">At least 8 characters.</p>
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Confirm new password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {pwError && <p className="text-sm text-red-600">{pwError}</p>}
          {pwSuccess && <p className="text-sm text-green-600">{pwSuccess}</p>}

          <button
            type="submit"
            disabled={pwSubmitting}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-60"
          >
            {pwSubmitting ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </section>

      {/* Danger zone */}
      <section className="bg-white rounded-lg shadow-sm border border-red-200 p-4 sm:p-6">
        <h2 className="font-semibold text-red-700 mb-1">Danger zone</h2>
        <p className="text-sm text-slate-500 mb-3">
          Permanently delete your account. This action cannot be undone.
        </p>

        {isSuperuser ? (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            Superuser accounts must be removed by another superuser from the
            Users admin page.
          </p>
        ) : !deleteOpen ? (
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
          >
            Delete my account…
          </button>
        ) : (
          <form onSubmit={onDeleteAccount} className="space-y-3">
            <div>
              <label className="block text-sm text-slate-600 mb-1">
                Type <span className="font-mono font-semibold">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-600 mb-1">Password</label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                autoComplete="current-password"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={deleteSubmitting}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-60"
              >
                {deleteSubmitting ? 'Deleting…' : 'Permanently delete account'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDeleteOpen(false);
                  setDeletePassword('');
                  setDeleteConfirmText('');
                  setDeleteError('');
                }}
                className="px-4 py-2 bg-slate-100 text-slate-700 text-sm rounded hover:bg-slate-200"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
};

export default ProfilePage;
